import React from 'react';
import { Link } from 'react-router-dom';
import Background from '../components/Background';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />
      
      <div className="relative z-10 min-h-screen flex flex-col items-center py-8 md:py-12 px-4 sm:px-6">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <Link 
              to="/" 
              className="inline-block mb-6 text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 font-elegant text-sm tracking-luxury-tight"
            >
              ← Back to Home
            </Link>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-gradient-gold mb-4 tracking-luxury">
              Privacy Policy
            </h1>
            <p className="text-sm text-text-secondary/70 font-sans">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="glass-grain rounded-2xl p-6 md:p-8 lg:p-10 backdrop-blur-xl border border-cosmic-gold/20 space-y-6 md:space-y-8">
            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Introduction
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                Welcome to Aivaris ("we," "our," or "us"). We are committed to protecting your privacy and ensuring you have a positive experience on our platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI Astrologer service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Information We Collect
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Personal Information</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    When you sign up for Aivaris, we collect information such as your name, email address, and authentication credentials through our third-party authentication provider (Supabase/Google OAuth).
                  </p>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Birth Chart Data</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    To provide personalized astrological readings, we collect your birth date, time, and location. This information is essential for calculating your natal chart and generating accurate astrological insights.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-heading text-text-primary mb-2">Usage Data</h3>
                  <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                    We automatically collect information about how you interact with our service, including questions asked, readings generated, and session history. This helps us improve our service and provide better user experiences.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                How We Use Your Information
              </h2>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li>To calculate and generate personalized astrological readings based on your birth chart</li>
                <li>To provide and maintain our service, including authentication and user account management</li>
                <li>To improve our AI models and astrological algorithms</li>
                <li>To communicate with you about your account, service updates, and support requests</li>
                <li>To analyze usage patterns and optimize our platform's performance</li>
                <li>To comply with legal obligations and protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Data Storage and Security
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li>All data is encrypted in transit using HTTPS/TLS</li>
                <li>Sensitive information is stored securely in our database with access controls</li>
                <li>We use secure authentication providers (Supabase) for user authentication</li>
                <li>Regular security audits and updates to protect against vulnerabilities</li>
              </ul>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mt-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Third-Party Services
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                We use the following third-party services that may have access to your information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li><strong>Supabase:</strong> For user authentication and database services</li>
                <li><strong>OpenAI/Anthropic:</strong> For AI-powered astrological reading generation</li>
                <li><strong>Vercel:</strong> For hosting and deployment of our frontend application</li>
              </ul>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mt-4">
                These services have their own privacy policies governing how they handle your information. We encourage you to review their privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Your Rights
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                <li>Access and review your personal information</li>
                <li>Update or correct inaccurate information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt out of certain data collection practices</li>
                <li>Export your data in a portable format</li>
              </ul>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed mt-4">
                To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Data Retention
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide you with our services. If you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal or regulatory purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Children's Privacy
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately so we can delete that information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Changes to This Privacy Policy
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-heading text-gradient-gold mb-4 tracking-luxury">
                Contact Us
              </h2>
              <p className="text-sm md:text-base text-text-secondary/80 font-sans leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-sm md:text-base text-cosmic-gold font-sans mt-2">
                Email: kaashika2599@gmail.com
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 md:mt-12">
            <Link 
              to="/" 
              className="inline-block text-cosmic-gold hover:text-cosmic-gold/80 transition-colors duration-300 font-elegant text-sm tracking-luxury-tight"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

